import { ref, computed } from "vue";
import { defineStore } from "pinia";
import {
  fetchDetailedUserData,
  fetchImpersonatedDetailedUserData,
  parseOneIdClaim,
  type DetailedUserData,
  type OneIdClaim,
  type Organization,
} from "./user-management";

export interface ExternalIdInfo {
  isPoweredByUser: boolean;
  isPoweredBySubAccountUser: boolean;
  resourceName: string | null;
}

const ONEID_CLAIM_STORAGE_KEY = "shipbob-oneid-claim";

/**
 * User context (logged-in + impersonated user, OneID claim) from the
 * user-management API. Loaded by the auth store once a session exists; the
 * rbac ability and the legacy $hostStore shim both read from here.
 */
export const useUserStore = defineStore("user", () => {
  const loggedInUser = ref<DetailedUserData | null>(null);
  const impersonatedUser = ref<DetailedUserData | null>(null);
  const oneIdClaim = ref<OneIdClaim | null>(null);
  const isAdmin = ref(false);
  const isLoading = ref(false);
  const isInitialized = ref(false);

  const displayName = computed(() => loggedInUser.value?.Name ?? "");
  const email = computed(() => loggedInUser.value?.Email ?? "");
  const roles = computed(() => loggedInUser.value?.Roles ?? []);
  const organizations = computed<Organization[]>(() => loggedInUser.value?.Organizations ?? []);
  /** Permission ids from the OneID claim (rbac source for reference-token clients). */
  const permissions = computed<string[]>(() => oneIdClaim.value?.Perm ?? []);

  const externalIdInfo = computed<ExternalIdInfo>(() => {
    const atts = oneIdClaim.value?.org_atts;
    if (!atts) {
      return { isPoweredByUser: false, isPoweredBySubAccountUser: false, resourceName: null };
    }
    const isPoweredByUser = atts.external_type_id === "1";
    const isPoweredBySubAccountUser = isPoweredByUser && !!atts.parent_org_id;
    return {
      isPoweredByUser,
      isPoweredBySubAccountUser,
      resourceName: isPoweredBySubAccountUser ? (atts.resource_id ?? null) : null,
    };
  });

  async function initializeUserContext(options?: { force?: boolean }) {
    if (isInitialized.value && !options?.force) return;
    isLoading.value = true;
    try {
      const [detailed, impersonated] = await Promise.allSettled([
        fetchDetailedUserData(),
        fetchImpersonatedDetailedUserData(),
      ]);

      if (detailed.status === "rejected") {
        console.error("[User] Detailed user fetch failed:", detailed.reason);
      }

      loggedInUser.value = detailed.status === "fulfilled" ? detailed.value : null;
      impersonatedUser.value = impersonated.status === "fulfilled" ? impersonated.value : null;

      if (loggedInUser.value) {
        oneIdClaim.value = parseOneIdClaim(loggedInUser.value.OneIdClaim);
        isAdmin.value = loggedInUser.value.IsAdmin ?? false;
        if (oneIdClaim.value) {
          try {
            localStorage.setItem(ONEID_CLAIM_STORAGE_KEY, JSON.stringify(oneIdClaim.value));
          } catch {
            /* storage unavailable */
          }
        }
        // Only a successful load counts as initialized, so a failed fetch is retried next time.
        isInitialized.value = true;
      }
    } finally {
      isLoading.value = false;
    }
  }

  function clearUserContext() {
    loggedInUser.value = null;
    impersonatedUser.value = null;
    oneIdClaim.value = null;
    isAdmin.value = false;
    isInitialized.value = false;
    try {
      localStorage.removeItem(ONEID_CLAIM_STORAGE_KEY);
    } catch {
      /* storage unavailable */
    }
  }

  return {
    loggedInUser,
    impersonatedUser,
    oneIdClaim,
    isAdmin,
    isLoading,
    isInitialized,
    displayName,
    email,
    roles,
    organizations,
    permissions,
    externalIdInfo,
    initializeUserContext,
    clearUserContext,
  };
});
